import { isValid, intervalToDuration, formatDuration } from 'date-fns';
import { DATE_RANGES_TYPES } from 'Common/enums';
import { ONE_DAY_STRING, TWENTY_FOUR_HOURS_STRING } from '../utils/constants';

type Options = Intl.DateTimeFormatOptions;

// if you specify default, that means just use what the browser has (fallback for when the config isn't set up)
let locale = 'default';
let currencyCode: string = 'USD';

export function initLocaleService(configLocale: string, configCurrencyCode: string) {
    locale = configLocale;
    currencyCode = configCurrencyCode;
}

const dateOrNull = (str?: string) => (isValid(new Date(str ?? '')) ? new Date(str ?? '') : null);

function getLocaleNumber(value: number, options?: Intl.NumberFormatOptions) {
    if (typeof value !== 'number') return value;

    return new Intl.NumberFormat(locale, options).format(value);
}

function getLocaleCurrency(value: number, options?: Intl.NumberFormatOptions) {
    if (typeof value !== 'number') return value;

    return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode, ...options }).format(value);
}

function getDateTime(
    str?: string,
    options: Options = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
    }
) {
    const date = dateOrNull(str);
    if (!date) return null;
    return new Intl.DateTimeFormat(locale, options).format(date);
}

function getDate(str?: string, options: Options = {}) {
    const date = dateOrNull(str);
    if (!date) return null;
    return new Intl.DateTimeFormat(locale, options).format(date);
}

function getTime(str?: string, options: Options = { hour: 'numeric', minute: 'numeric' }) {
    const date = dateOrNull(str);
    if (!date) return null;
    return new Intl.DateTimeFormat(locale, options).format(date);
}

function getMonthAndYear(year: number, month: number) {
    const date = new Date(year, month);
    return getDate(date.toDateString(), { year: 'numeric', month: 'long' });
}

function getReportDateRange(
    dateRangeType: valueof<typeof DATE_RANGES_TYPES>,
    dateRangeValue: DateRangeValue,
    dateRangeLabel: string
): string {
    switch (dateRangeType) {
        case DATE_RANGES_TYPES.YEAR: {
            const date = new Date(dateRangeValue as number, 0);
            return getDate(date.toDateString(), { year: 'numeric' }) || dateRangeLabel;
        }
        case DATE_RANGES_TYPES.MONTH: {
            const { year, month } = (dateRangeValue as MonthDateRangeTypeValue) || {};
            return getMonthAndYear(year, month) || dateRangeLabel;
        }
        case DATE_RANGES_TYPES.CUSTOM: {
            const { endDate, startDate } = (dateRangeValue as CustomDateRange) || {};
            if (endDate && startDate) {
                return `${getDate(startDate.toString())} - ${getDate(endDate.toString())}`;
            }
            return dateRangeLabel;
        }
        default: {
            return dateRangeLabel;
        }
    }
}

function getDuration(start: string, end: string, truncateSeconds?: boolean): string {
    const durations = intervalToDuration({ start: new Date(start), end: new Date(end) });
    const formattedDuration = formatDuration(durations);
    // Special case where we display '24 hours' instead of '1 day' to match the
    // duration format in velocity counters in Global Rules.
    if (formattedDuration === ONE_DAY_STRING) return TWENTY_FOUR_HOURS_STRING;

    if (!truncateSeconds) {
        return formattedDuration;
    }

    durations.seconds = 0;
    return formatDuration(durations);
}

export const NumberService = { getLocaleNumber, getLocaleCurrency };
export const DateService = {
    getDateTime,
    getDate,
    getTime,
    getReportDateRange,
    getMonthAndYear,
    dateOrNull,
    getDuration
};
